import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

export default function SheetRecords() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchFrom, setSearchFrom] = useState('');
  const [searchTo, setSearchTo] = useState('');
  const [showEntries, setShowEntries] = useState('All');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [autoDateRangeSet, setAutoDateRangeSet] = useState(false);

  useEffect(() => { 
    console.log('Sheet Records component mounted, calling fetchList');
    fetchList(); 
  }, []);

  async function fetchList() {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching sheet records from /api/sheet-records with date range:', searchFrom, 'to', searchTo);
      
      // Use date filters if set, otherwise fetch all data for auto-determination
      const params = new URLSearchParams();
      if (searchFrom) params.append('from', searchFrom);
      if (searchTo) params.append('to', searchTo);
      
      const res = await fetch(`/api/sheet-records?${params}`, { credentials: 'include' });
      console.log('Sheet Records response status:', res.status);
      const d = await res.json();
      console.log('Sheet Records fetch response:', d);
      
      if (d.ok) {
        console.log('Sheet Records rows:', d.rows);
        if (d.rows && d.rows.length > 0) {
          // Auto-set date range based on actual data (only if not already set)
          if (!autoDateRangeSet && !searchFrom && !searchTo) {
            const dates = d.rows.map((r: any) => r.date || r.sale_date).filter(Boolean);
            if (dates.length > 0) {
              const minDate = dates.reduce((a: string, b: string) => a < b ? a : b);
              const maxDate = dates.reduce((a: string, b: string) => a > b ? a : b);
              setSearchFrom(minDate);
              setSearchTo(maxDate);
              setAutoDateRangeSet(true);
              console.log('Auto-set date range:', minDate, 'to', maxDate);
            }
          }
          setRows(d.rows);
        } else {
          // If sheet-records is empty, try fetching from sale-entries as fallback
          console.log('Sheet records empty, trying sale-entries as fallback');
          const saleParams = new URLSearchParams();
          if (searchFrom) saleParams.append('from', searchFrom);
          if (searchTo) saleParams.append('to', searchTo);
          
          const saleRes = await fetch(`/api/sale-entries?${saleParams}`, { credentials: 'include' });
          const saleData = await saleRes.json();
          console.log('Sale entries response:', saleData);
          if (saleData.ok && saleData.rows) {
            // Auto-set date range based on sale entries (only if not already set)
            if (!autoDateRangeSet && !searchFrom && !searchTo && saleData.rows.length > 0) {
              const dates = saleData.rows.map((r: any) => r.sale_date).filter(Boolean);
              if (dates.length > 0) {
                const minDate = dates.reduce((a: string, b: string) => a < b ? a : b);
                const maxDate = dates.reduce((a: string, b: string) => a > b ? a : b);
                setSearchFrom(minDate);
                setSearchTo(maxDate);
                setAutoDateRangeSet(true);
                console.log('Auto-set date range from sale entries:', minDate, 'to', maxDate);
              }
            }
            // Transform sale entries to match sheet records format
            const transformed = saleData.rows.map((r: any) => ({
              id: r.id,
              date: r.sale_date,
              shift: r.shift,
              employee: r.employee_name,
              entry_source: 'System',
              modules: 'Sale Entry',
              action: 'Created',
              user_log_details: `Tank: ${r.tank_number}, Product: ${r.product_name}, Nozzle: ${r.nozzle_number}`
            }));
            setRows(transformed);
          } else {
            setRows([]);
          }
        }
      } else {
        setError(d.error || 'Failed to fetch sheet records');
        console.error('Sheet Records fetch error:', d);
      }
    } catch (e) { 
      console.error('Failed to fetch sheet records', e);
      setError('Network error: Failed to fetch sheet records');
    } finally {
      setLoading(false);
    }
  }

  const filteredRows = rows.filter((r) => {
    const search = searchText.toLowerCase();
    if (!search) return true;
    return Object.values(r).some((v) => String(v).toLowerCase().includes(search));
  });

  const pageSize = showEntries === 'All' ? filteredRows.length : parseInt(showEntries);
  const startIndex = (currentPage - 1) * pageSize;
  const displayedRows = filteredRows.slice(startIndex, startIndex + pageSize);

  const totalPages = Math.ceil(filteredRows.length / (showEntries === 'All' ? 1 : parseInt(showEntries))) || 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white font-bold">
              📋
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">Sheet Records</div>
              <div className="text-sm text-gray-500">Entries & Log Management</div>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Logged in as: dfedf@gmail.com
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white border-b px-6 py-2">
        <nav className="text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2">
            <li>
              <span className="text-gray-500">Dashboard</span>
            </li>
            <li className="flex items-center">
              <span className="mx-2 text-gray-400">&gt;</span>
              <span className="text-gray-900 font-medium">Entries</span>
            </li>
          </ol>
        </nav>
      </div>

      <div className="p-6">
        {/* Search Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Search From</label>
              <input 
                type="date" 
                value={searchFrom}
                onChange={(e) => setSearchFrom(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 w-40 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">To</label>
              <input 
                type="date" 
                value={searchTo}
                onChange={(e) => setSearchTo(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 w-40 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
            </div>
            <button 
              onClick={fetchList}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search
            </button>
          </div>
        </div>

        {/* Table Controls */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Show</label>
                <select 
                  value={showEntries}
                  onChange={(e) => setShowEntries(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1 w-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>All</option>
                  <option>10</option>
                  <option>25</option>
                  <option>50</option>
                  <option>100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Search:</label>
              <input 
                type="text" 
                placeholder="Search records..." 
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">S.No.</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Shift</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Employee</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Entry Source</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Modules</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Action</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">User Log Details</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        No data available in table
                      </td>
                    </tr>
                  ) : displayedRows.map((r, i) => (
                    <tr key={r.id} className="border-b">
                      <td className="px-4 py-3 text-sm">{i + 1}</td>
                      <td className="px-4 py-3 text-sm">{r.date || r.sheet_name || '-'}</td>
                      <td className="px-4 py-3 text-sm">{r.shift || '-'}</td>
                      <td className="px-4 py-3 text-sm">{r.employee || '-'}</td>
                      <td className="px-4 py-3 text-sm">{r.entry_source || '-'}</td>
                      <td className="px-4 py-3 text-sm">{r.modules || '-'}</td>
                      <td className="px-4 py-3 text-sm">{r.action || '-'}</td>
                      <td className="px-4 py-3 text-sm">{r.user_log_details || r.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-700">
            Showing {displayedRows.length > 0 ? 1 : 0} to {displayedRows.length} of {filteredRows.length} entries
          </div>
          <div className="flex items-center gap-2">
            <button 
              className="border border-gray-300 rounded px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50" 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm">{currentPage}</button>
            <button 
              className="border border-gray-300 rounded px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
